import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import DateField from "@/components/DateField";

// A single dialog used for both CREATE and EDIT of custom checklist items.
// The parent passes:
//   - open, onOpenChange
//   - initial: {text?, deadline?, why?, how?, warning?}  (empty for create)
//   - mode: "create" | "edit"
//   - onSubmit(payload): Promise<void>   payload contains only string fields
export default function CustomItemDialog({ t, open, onOpenChange, mode, initial, onSubmit }) {
  const [text, setText] = useState("");
  const [deadline, setDeadline] = useState(null);
  const [why, setWhy] = useState("");
  const [how, setHow] = useState("");
  const [warning, setWarning] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setText(initial?.text || "");
      setDeadline(initial?.deadline || null);
      setWhy(initial?.why || "");
      setHow(initial?.how || "");
      setWarning(initial?.warning || "");
      setErr("");
      setSaving(false);
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      setErr(t.modes.organizzativa.customTitleRequired);
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        text: text.trim(),
        deadline: deadline || null,
        why: why.trim() || null,
        how: how.trim() || null,
        warning: warning.trim() || null,
      });
      onOpenChange(false);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "edit"
    ? t.modes.organizzativa.customFormTitleEdit
    : t.modes.organizzativa.customFormTitleCreate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="custom-item-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-xl text-ink">{title}</DialogTitle>
          <DialogDescription className="text-sm text-ink-soft font-sans">
            {t.modes.organizzativa.customFieldTitle} *
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="ci-title" className="text-xs uppercase tracking-widest text-ink-soft font-semibold">
              {t.modes.organizzativa.customFieldTitle} *
            </Label>
            <Input
              id="ci-title"
              data-testid="custom-field-title"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t.modes.organizzativa.customFieldTitlePh}
              className="rounded-lg"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ci-deadline" className="text-xs uppercase tracking-widest text-ink-soft font-semibold">
              {t.modes.organizzativa.customFieldDeadline}
            </Label>
            <DateField
              value={deadline}
              onChange={setDeadline}
              testid="custom-field-deadline"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ci-why" className="text-xs uppercase tracking-widest text-terracotta font-semibold">
              {t.modes.organizzativa.customFieldWhy}
            </Label>
            <Textarea
              id="ci-why"
              data-testid="custom-field-why"
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              placeholder={t.modes.organizzativa.customFieldWhyPh}
              className="rounded-lg min-h-[70px]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ci-how" className="text-xs uppercase tracking-widest text-sage font-semibold">
              {t.modes.organizzativa.customFieldHow}
            </Label>
            <Textarea
              id="ci-how"
              data-testid="custom-field-how"
              value={how}
              onChange={(e) => setHow(e.target.value)}
              placeholder={t.modes.organizzativa.customFieldHowPh}
              className="rounded-lg min-h-[70px]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ci-warning" className="text-xs uppercase tracking-widest text-ink-soft font-semibold">
              {t.modes.organizzativa.customFieldWarning}
            </Label>
            <Textarea
              id="ci-warning"
              data-testid="custom-field-warning"
              value={warning}
              onChange={(e) => setWarning(e.target.value)}
              placeholder={t.modes.organizzativa.customFieldWarningPh}
              className="rounded-lg min-h-[70px]"
            />
          </div>
          {err && (
            <div data-testid="custom-field-error" className="text-sm text-terracotta font-sans">{err}</div>
          )}
          <DialogFooter>
            <Button
              type="submit"
              data-testid="custom-form-save"
              disabled={saving}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold"
            >
              {t.modes.organizzativa.customFormSave}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
